<template>
  <div class="flex h-auto bg-gray-900 text-white">
    <!-- Sidebar -->
    <aside class="w-64 bg-gray-800 p-6 flex flex-col space-y-6 shadow-xl border border-gray-700 bg-gradient-to-b from-gray-800 to-gray-900">
      <div class="flex items-center justify-center space-x-3">
        <img src="@/assets/logo.svg" alt="Vultric Logo" class="h-20">
      </div>
      <Navbar />
    </aside>

    <!-- Main area -->
    <div class="flex-1 flex flex-col">
      <!-- Header -->
      <header class="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-xl font-semibold">Deploy New Server</h2>
            <p class="text-sm text-slate-400">Step {{ currentStep }} of {{ totalSteps }} — {{ stepTitles[currentStep - 1] }}</p>
          </div>

          <div class="text-right">
            <p class="text-sm text-slate-400">Estimated Cost</p>
            <p class="text-lg font-bold text-blue-400">${{ totalCost }}/mo</p>
          </div>
        </div>
      </header>

      <!-- Steps progress -->
      <div class="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div class="flex items-center justify-center space-x-6">
          <div v-for="(title, idx) in stepTitles" :key="idx" class="flex items-center">
            <div :class="progressCircleClass(idx+1)" class="w-10 h-10 rounded-full flex items-center justify-center">
              <template v-if="idx+1 < currentStep">
                <!-- check -->
                <svg class="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
              </template>
              <template v-else>{{ idx+1 }}</template>
            </div>
            <div v-if="idx < totalSteps - 1" :class="progressLineClass(idx+1)" class="w-12 h-px mx-3"></div>
          </div>
        </div>
      </div>

      <!-- Wizard content -->
      <main class="flex-1 p-6 overflow-auto">
        <div class="max-w-6xl mx-auto">
          <!-- Step 1: Compute Type -->
          <section v-if="currentStep === 1" class="space-y-6">
            <div class="text-center">
              <h3 class="text-2xl font-bold">Choose Your Compute Type</h3>
              <p class="text-slate-400">Pick a compute family that matches your workload.</p>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div v-for="type in serverTypesInfo" :key="type.key"
                @click="selectComputeType(type.key)"
                :class="[
                  'group relative p-6 border-2 rounded-2xl cursor-pointer transition-transform hover:scale-105',
                  form.computeType === type.key ? 'border-blue-500 bg-blue-600/10 shadow-lg' : 'border-slate-600'
                ]">
                <div class="flex flex-col items-center">
                  <div :class="['w-16 h-16 mb-4 rounded-xl flex items-center justify-center', type.color]">
                      <img v-if="type.icon" :src="require(`@/assets/types/${type.icon}.svg`)" class="w-7 h-7" />
                  </div>
                  <h4 class="text-lg font-semibold">{{ type.name }}</h4>
                  <p class="text-sm text-slate-400 text-center mt-2">{{ type.description }}</p>
                  <div class="mt-4 text-blue-400 font-semibold">Starting at ${{ type.startingPrice }}/mo</div>
                </div>

                <div v-if="form.computeType === type.key" class="absolute top-4 right-4">
                  <div class="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white">
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Step 2: Server configuration -->
          <section v-if="currentStep === 2" class="space-y-6">
            <div class="text-center">
              <h3 class="text-2xl font-bold">Server Configuration</h3>
              <p class="text-slate-400">Name your server and pick a datacenter location.</p>
            </div>

            <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Server Name</label>
                <input v-model="form.servername" type="text" placeholder="my-production-server"
                  class="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <p class="text-xs text-slate-500 mt-2">Choose a descriptive name for easy identification.</p>

                <div class="mt-6">
                  <label class="block text-sm font-medium text-slate-300 mb-2">Datacenter Location</label>
                  <select v-model="form.location" class="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white appearance-none">
                    <option value="">Select Location</option>
                    <option v-for="loc in locations" :key="loc" :value="loc">{{ loc }}</option>
                  </select>
                  <p class="text-xs text-slate-500 mt-2">Choose the location closest to your users.</p>
                </div>
              </div>

              <div class="bg-slate-700/30 rounded-xl p-6">
                <h4 class="text-white font-medium mb-4">Location Benefits</h4>
                <ul class="text-sm space-y-3 text-slate-300">
                  <li>• Lower latency for nearby users</li>
                  <li>• Compliance with regional regulations</li>
                  <li>• 99.9% SLA</li>
                </ul>
              </div>
            </div>
          </section>

          <!-- Step 3: Operating System -->
          <section v-if="currentStep === 3" class="space-y-6">
            <div class="text-center">
              <h3 class="text-2xl font-bold">Choose Operating System</h3>
              <p class="text-slate-400">Select your preferred OS and version.</p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div v-for="os in operatingSystems" :key="os" @click="selectOS(os.name)"
                :class="[
                  'group relative p-6 border-2 rounded-2xl cursor-pointer transition-transform hover:scale-105',
                  form.os === os.name ? 'border-blue-500 bg-blue-600/10 shadow-lg' : 'border-slate-600'
                ]">
                <div class="flex items-center space-x-4">
                  <div :class="['w-12 h-12 rounded-xl flex items-center justify-center', os.color]">
                    <img v-if="os.image" :src="require(`@/assets/operatingsystems/${os.name}.svg`)" class="w-7 h-7" />
                  </div>
                  <div>
                    <h4 class="text-white font-semibold">{{ os.name }}</h4>
                    <p class="text-slate-400 text-sm">{{ os.image }}</p>
                  </div>
                </div>
                <div class="mt-4 space-y-2">
                  <div v-for="f in os.features" :key="f" class="flex items-center space-x-2 text-slate-300 text-sm">
                    <svg class="w-4 h-4 text-green-400" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                    <span>{{ f }}</span>
                  </div>
                </div>

                <div v-if="form.os === os.name" class="absolute top-4 right-4">
                  <div class="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white">
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Step 4: Resource Plans -->
          <section v-if="currentStep === 4" class="space-y-6">
            <div class="text-center">
              <h3 class="text-2xl font-bold">Select Resource Plan</h3>
              <p class="text-slate-400">Choose the plan for your selected compute type.</p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div v-for="plan in currentPlans" :key="plan.name"
                @click="selectPlan(plan)"
                :class="[
                  'group relative p-6 border-2 rounded-2xl cursor-pointer transition-transform hover:scale-105',
                  form.plan === plan.name ? 'border-blue-500 bg-blue-600/10 shadow-lg' : 'border-slate-600'
                ]">
                <div class="text-center">
                  <div v-if="plan.popular" class="inline-block bg-gradient-to-r from-blue-500 to-indigo-500 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">
                    MOST POPULAR
                  </div>
                  <h4 class="text-xl font-bold">{{ plan.name }}</h4>
                  <div class="text-3xl font-bold text-blue-400">${{ plan.price }}</div>
                  <p class="text-slate-400 text-sm">per month</p>
                  <p class="text-slate-500 text-xs">
                    (~${{ (plan.price / 730).toFixed(4) }}/hr)
                  </p>
                </div>

                <div class="mt-4 space-y-3">
                  <div class="flex justify-between">
                    <span class="text-slate-300">{{ plan.cpuLabel }}</span>
                    <span class="text-white">{{ plan.cpu }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-slate-300">Memory</span>
                    <span class="text-white">{{ plan.ram }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="text-slate-300">Storage</span>
                    <span class="text-white">{{ plan.storage }}</span>
                  </div>
                </div>

                <div v-if="form.plan === plan.name" class="absolute top-4 right-4">
                  <div class="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white">
                    <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="!currentPlans.length" class="text-center text-slate-400 mt-6">
              No plans available for this compute type yet.
            </div>
          </section>

          <!-- Step 5: Extras -->
          <section v-if="currentStep === 5" class="space-y-6">
            <div class="text-center">
              <h3 class="text-2xl font-bold">Additional Services</h3>
              <p class="text-slate-400">Add optional services to your server.</p>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div v-for="option in extraOptions" :key="option.name"
                @click="toggleExtra(option.name)"
                :class="[
                  'p-6 border-2 rounded-2xl cursor-pointer transition-transform hover:scale-105 flex justify-between items-center',
                  form.extras.includes(option.name) ? 'border-blue-500 bg-blue-600/10' : 'border-slate-600'
                ]">
                <div>
                  <h4 class="font-medium">{{ option.name }}</h4>
                  <p class="text-slate-400 text-sm">{{ option.description || '' }}</p>
                </div>
                <div class="text-slate-300">
                  <span v-if="option.price > 0">+${{ option.price }}/mo</span>
                  <span v-else class="text-slate-400">Included</span>
                </div>
              </div>
            </div>
          </section>

          <!-- Step 6: Review & Deploy -->
          <section v-if="currentStep === 6" class="space-y-6">
            <div class="text-center">
              <h3 class="text-2xl font-bold">Review & Deploy</h3>
              <p class="text-slate-400">Check everything before placing your order.</p>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div class="space-y-6">
                <div class="bg-slate-800 border border-slate-700 rounded-xl p-6">
                  <h4 class="font-medium text-white mb-4">Configuration Summary</h4>
                  <div class="space-y-3 text-sm text-slate-300">
                    <div class="flex justify-between"><span>Compute Type</span><span>{{ getComputeTypeName() || '—' }}</span></div>
                    <div class="flex justify-between"><span>Server Name</span><span>{{ form.servername || '—' }}</span></div>
                    <div class="flex justify-between"><span>Location</span><span>{{ form.location || '—' }}</span></div>
                    <div class="flex justify-between"><span>Operating System</span><span>{{ form.os || '—' }}</span></div>
                    <div class="flex justify-between"><span>Plan</span><span>{{ form.plan || '—' }}</span></div>
                    <div class="flex justify-between"><span>Extras</span><span>{{ form.extras.length ? form.extras.join(', ') : 'None' }}</span></div>
                  </div>
                </div>

                <div class="bg-slate-800 border border-slate-700 rounded-xl p-6">
                  <h4 class="font-medium text-white mb-4">Deployment Timeline</h4>
                  <div class="space-y-2 text-sm text-slate-300">
                    <div>• Server provisioning: ~2–5 minutes</div>
                    <div>• OS installation: ~2–4 minutes</div>
                    <div>• Network config: ~1 minute</div>
                  </div>
                </div>
              </div>

              <div class="space-y-6">
                <div class="bg-slate-800 border border-slate-700 rounded-xl p-6">
                  <h4 class="font-medium text-white mb-4">Cost Breakdown</h4>
                  <div class="space-y-3 text-sm text-slate-300">
                    <div class="flex justify-between"><span>Base Plan ({{ form.plan || '—' }})</span><span>${{ selectedPlanPrice }}/mo</span></div>
                    <div v-for="e in activeExtrasDetails" :key="e.name" class="flex justify-between">
                      <span>{{ e.name }}</span><span>${{ e.price }}/mo</span>
                    </div>
                    <hr class="border-slate-600">
                    <div class="flex justify-between text-lg font-semibold text-white">
                      <span>Total Monthly Cost</span><span class="text-blue-400">${{ totalCost }}/mo</span>
                    </div>
                    <div class="text-xs text-slate-500">* Billed hourly. Cancel anytime.</div>
                  </div>
                </div>

                <div class="space-y-3">
                  <button @click="placeOrder"
                    :disabled="placing || !isFormValid"
                    class="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-indigo-600 hover:to-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all disabled:opacity-60">
                    <template v-if="placing">
                      <svg class="animate-spin -ml-1 mr-3 h-5 w-5 inline-block" viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="10" stroke-width="4" stroke="currentColor" class="opacity-25"/></svg>
                      Deploying...
                    </template>
                    <template v-else>Deploy Server</template>
                  </button>
                </div>
              </div>
            </div>
          </section>

          <!-- Navigation -->
          <div class="flex justify-between mt-10">
            <button v-if="currentStep > 1" @click="previousStep"
              class="px-6 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl">
              ← Previous
            </button>

            <div class="ml-auto">
              <button v-if="currentStep < totalSteps" @click="nextStep" :disabled="!canProceed"
                class="px-6 py-3 bg-blue-500 hover:bg-blue-600 rounded-xl disabled:opacity-50">
                Next →
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Toasts -->
    <ToastManager ref="toastManager" />
  </div>
</template>

<script>
import axios from 'axios';
import Navbar from '@/components/nav.vue';
import ToastManager from '@/components/ToastManager.vue';

export default {
  name: 'OrderPageWizard',
  components: { Navbar, ToastManager },
  data() {
    return {
      // Steps
      currentStep: 1,
      totalSteps: 6,
      stepTitles: [
        'Compute Type',
        'Configuration',
        'Operating System',
        'Resource Plans',
        'Additional Services',
        'Review & Deploy'
      ],

      // form
      form: {
        computeType: '',
        servername: '',
        location: '',
        os: '',
        plan: '',
        extras: []
      },

      // UI data
      serverTypesInfo: [
        { key: 'Shared CPU', name: 'Shared CPU', description: 'Cost-effective instances with shared CPU', startingPrice: 1.25, color: 'bg-blue-600', icon: 'server-solid-full' },
        { key: 'Dedicated CPU', name: 'Dedicated CPU', description: 'Guaranteed CPU performance', startingPrice: 6.00, color: 'bg-green-600', icon: 'microchip-solid-full' },
        { key: 'Cloud GPU', name: 'Cloud GPU', description: 'GPU-accelerated instances for ML', startingPrice: 90.00, color: 'bg-purple-600', icon: 'video-solid-full' },
        { key: 'Bare Metal', name: 'Bare Metal', description: 'Physical servers with full resources', startingPrice: 250.00, color: 'bg-orange-600', icon: 'box-solid-full' }
      ],

      // locations and OS options (from your original file)
      locations: ['Ashburn, VA', 'Miami, FL', 'Dallas, TX', 'Phoenix, AZ', 'Los Angeles, CA', 'London, UK', 'MacPherson, SG'],
      operatingSystems: [
        { name: 'Ubuntu 22.04', image: 'Ubuntu 22.04 LTS', features: ['LTS', 'Docker ready'], color: 'bg-white-600' },
        { name: 'Debian 11', image: 'Debian 11', features: ['Stable', 'Minimal'], color: 'bg-white-600' },
        { name: 'Windows Server', image: 'Windows Server 2019', features: ['GUI', '.NET support'], color: 'bg-white-600' },
        { name: 'CentOS 7', image: 'CentOS 7', features: ['RHEL compatible'], color: 'bg-white-600' }
      ],

      // plans loaded from API for the selected compute type
      selectedPlans: [],

      // extras (kept similar to your original)
      extraOptions: [
        { name: 'VPC Network', price: 0, description: 'Private network for your server' },
        { name: 'IPv6', price: 0, description: 'Add IPv6 support' },
        { name: 'Backups', price: 2.50, description: 'Automated daily backups' }
      ],

      // API/loading states
      fetchingPlans: false,
      placing: false
    };
  },
  computed: {
    // compute current plans based on selected compute type key (map serverTypesInfo keys to API types if needed)
    currentPlans() {
      return this.selectedPlans || [];
    },
    canProceed() {
      switch (this.currentStep) {
        case 1: return !!this.form.computeType;
        case 2: return !!(this.form.servername && this.form.location);
        case 3: return !!this.form.os;
        case 4: return !!this.form.plan;
        case 5: return true;
        case 6: return true;
        default: return false;
      }
    },
    isFormValid() {
      return this.form.computeType && this.form.servername && this.form.location && this.form.os && this.form.plan;
    },
    // pricing helpers
    selectedPlanPrice() {
      const p = this.currentPlans.find(pl => pl.name === this.form.plan);
      return p ? Number(p.price).toFixed(2) : '0.00';
    },
    activeExtrasDetails() {
      return this.extraOptions.filter(e => this.form.extras.includes(e.name));
    },
    totalCost() {
      let total = 0;
      const base = this.currentPlans.find(pl => pl.name === this.form.plan);
      if (base && base.price) total += Number(base.price);
      this.activeExtrasDetails.forEach(e => {
        total += Number(e.price || 0);
      });
      return total.toFixed(2);
    },
    userName() {
      // fallback values: you can replace with actual auth value if present
      return 'Account User';
    },
    userEmail() {
      return 'user@example.com';
    },
    userInitial() {
      return (this.userName && this.userName[0]) ? this.userName[0].toUpperCase() : 'U';
    }
  },
  methods: {
    // progress helpers classes
    progressCircleClass(stepNumber) {
      if (stepNumber < this.currentStep) return 'bg-green-600 text-white';
      if (stepNumber === this.currentStep) return 'bg-blue-500 text-white';
      return 'bg-slate-600 text-slate-400';
    },
    progressLineClass(stepNumber) {
      return stepNumber < this.currentStep ? 'bg-green-600' : 'bg-slate-600';
    },

    // select compute type -> fetch plans from API
    async selectComputeType(type) {
      this.form.computeType = type;
      // map to the API endpoint expected type param if necessary; your original used `type` directly
      try {
        this.fetchingPlans = true;
        const encoded = encodeURIComponent(type);
        const res = await axios.get(`https://api.syniac.com:3000/api/plan/${encoded}`, { withCredentials: true });
        // expect response.data.plans similar to original
        this.selectedPlans = res.data.plans || [];
        // auto-select first plan if exists
        if (this.selectedPlans.length) this.form.plan = this.selectedPlans[0].name;
        else this.form.plan = '';
      } catch (err) {
        console.error('Error fetching plans:', err);
        this.$refs.toastManager.addToast('Error fetching plans', 'error');
        this.selectedPlans = [];
      } finally {
        this.fetchingPlans = false;
      }
    },

    selectOS(osName) {
      this.form.os = osName;
    },

    selectPlan(plan) {
      this.form.plan = plan.name;
    },

    toggleExtra(name) {
      const i = this.form.extras.indexOf(name);
      if (i > -1) this.form.extras.splice(i, 1);
      else this.form.extras.push(name);
    },

    nextStep() {
      if (this.canProceed && this.currentStep < this.totalSteps) {
        this.currentStep++;
        // If moving to plans step and plans are empty, try fetching plans
        if (this.currentStep === 4 && !this.selectedPlans.length && this.form.computeType) {
          // fetch plans again
          this.selectComputeType(this.form.computeType);
        }
      } else {
        this.$refs.toastManager.addToast('Please complete the required fields to continue', 'error');
      }
    },

    previousStep() {
      if (this.currentStep > 1) this.currentStep--;
    },

    getComputeTypeName() {
      const t = this.serverTypesInfo.find(s => s.key === this.form.computeType);
      return t ? t.name : '';
    },

    async placeOrder() {
      if (!this.isFormValid) {
        this.$refs.toastManager.addToast('Please complete all required fields', 'error');
        return;
      }

      this.placing = true;
      try {
        const payload = {
          plan: this.form.plan,
          location: this.form.location,
          os: this.form.os,
          options: [], // keep compatibility with your API signature
          extras: this.form.extras,
          price: this.totalCost,
          servername: this.form.servername,
          computeType: this.form.computeType
        };

        await axios.post('https://api.syniac.com:3000/api/order/create', payload, { withCredentials: true });
        this.$refs.toastManager.addToast('Order placed successfully!', 'success');
        // optional: reset or navigate away
        this.resetForm();
      } catch (err) {
        console.error('Order error:', err);
        this.$refs.toastManager.addToast('Error placing order!', 'error');
      } finally {
        this.placing = false;
      }
    },

    resetForm() {
      this.form = {
        computeType: '',
        servername: '',
        location: '',
        os: '',
        plan: '',
        extras: []
      };
      this.selectedPlans = [];
      this.currentStep = 1;
    }
  },
  mounted() {
    // initialize with Dedicated CPU as your original did
    this.selectComputeType('Shared CPU');
    // set default location & os if desired
    this.form.location = this.locations[0];
    this.form.os = this.operatingSystems[0].name;
  }
};
</script>

<style scoped>
/* small helper to ensure max width and nicer spacing; adapt as needed */
</style>
